import { useReportDateState } from "../hooks/useReportDateAccess";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toLocalYmd } from "../utils/localDate";
import { formatStoredMoney } from "../utils/currency";
import { API_BASE_URL } from "../config/constants";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import {
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaFileExcel,
  FaPrint,
  FaUserFriends,
  FaMoneyBillWave,
  FaClipboardList,
  FaTimes,
} from "react-icons/fa";
import { downloadExcel, downloadPDF, printThermal, printThermalViaUsb } from "../utils/exportHelpers";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportControlBar from "../components/ReportControlBar";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { getOfflineCreditPaymentReport } from "../offline/financeStore";

const COLOR = {
  primary: "var(--app-primary)",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const DEFAULT_REPORT = (date) => ({
  date,
  restaurant: "",
  total_credit_orders: 0,
  total_credit_amount: 0,
  customer_count: 0,
  summary_by_customer: [],
  orders: [],
});

const CreditPaymentReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [date, setDate] = useReportDateState(today());
  const [report, setReport] = useState(DEFAULT_REPORT(today()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}").data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, date]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      let json;
      if (isFullOfflineModeEnabled()) {
        json = await getOfflineCreditPaymentReport(userId, date);
      } else {
        const res = await fetch(
          `${API_BASE_URL}/report-v2/credit-payments`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid: userId, date }),
          }
        );
        json = await res.json();
        if (!res.ok || json?.success === false) {
          throw new Error(json?.message || "Failed to load report");
        }
      }
      setReport({
        ...DEFAULT_REPORT(date),
        date: json?.date || date,
        restaurant: json?.restaurant || "",
        total_credit_orders: Number(json?.total_credit_orders ?? 0),
        total_credit_amount: Number(json?.total_credit_amount ?? 0),
        customer_count: Number(json?.customer_count ?? 0),
        summary_by_customer: Array.isArray(json?.summary_by_customer)
          ? json.summary_by_customer
          : [],
        orders: Array.isArray(json?.orders) ? json.orders : [],
      });
    } catch (e) {
      toast.error(e.message || "Unable to load report");
      setReport(DEFAULT_REPORT(date));
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const changeDate = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(toISO(d));
  };

  const fmtCurrency = formatStoredMoney;

  const formatSplitLines = (split) => {
    const lines = [];
    if (!split || typeof split !== "object") return lines;
    if (Number(split.cash) > 0) lines.push(`Cash: ${fmtCurrency(split.cash)}`);
    if (Number(split.upi) > 0) lines.push(`UPI: ${fmtCurrency(split.upi)}`);
    if (Number(split.card) > 0) lines.push(`Card: ${fmtCurrency(split.card)}`);
    if (Number(split.credit) > 0) lines.push(`Credit: ${fmtCurrency(split.credit)}`);
    return lines;
  };

  const getCustomerName = (row) =>
    row?.party_name || row?.customer_name || "Walk-in / Unassigned";

  const getCustomerPhone = (row) => row?.party_phone || row?.customer_phone || "";

  const getCustomerCompany = (row) =>
    row?.party_company_name || row?.customer_company_name || "";

  const getPaymentModeLabel = (row) => {
    const raw = String(row?.normalized_payment_mode || row?.payment_mode || "")
      .trim()
      .toLowerCase();
    if (raw.includes("split")) return "Split";
    if (raw.includes("upi")) return "UPI";
    if (raw.includes("card")) return "Card";
    if (raw.includes("credit")) return "Credit";
    return "Cash";
  };

  const summaryRows = useMemo(
    () => (Array.isArray(report?.summary_by_customer) ? report.summary_by_customer : []),
    [report]
  );

  const hasData = Array.isArray(report?.orders) && report.orders.length > 0;

  const exportExcel = () => {
    if (!hasData) {
      toast.info("No data to export.");
      return;
    }
    const headers = [
      "#",
      "Bill No",
      "Customer",
      "Phone",
      "Company",
      "Payment Mode",
      "Table",
      "Credit Amount",
      "Bill Total",
      "Date",
    ];
    const rows = (report.orders || []).map((order, idx) => [
      idx + 1,
      order.ordercode || "-",
      getCustomerName(order),
      getCustomerPhone(order) || "-",
      getCustomerCompany(order) || "-",
      getPaymentModeLabel(order),
      order.table_name || "-",
      order.credit_amount || 0,
      order.bill_amount || order.totalamount || 0,
      order.created_at ? new Date(order.created_at).toLocaleString("en-IN") : "-",
    ]);
    downloadExcel(`credit_payment_report_${date}.xlsx`, headers, rows);
  };

  const exportPdf = async () => downloadPDF(reportRef.current, `credit_payment_report_${date}.pdf`);

  const handlePrint = async () => {
    if (!hasData) {
      toast.info("No data to print.");
      return;
    }
    await printThermal(reportRef.current, `credit_payment_report_${date}`);
  };

  const handleUsbPrint = async () => {
    if (!hasData) {
      toast.info("No data to print.");
      return;
    }
    await printThermalViaUsb(reportRef.current, `credit_payment_report_${date}`);
  };

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <ReportPageHeader
        title="Credit Payment Report"
        subtitle="All credit payments for the selected date with customer details."
        stats={[
          { label: "Credit Orders", value: report.total_credit_orders || 0 },
          { label: "Customers", value: report.customer_count || 0 },
          { label: "Credit Amount", value: fmtCurrency(report.total_credit_amount) },
        ]}
      />

      <ReportControlBar>
        <ReportPeriodPicker date={date} onDateChange={(d) => setDate(d)} />
        <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
          {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>

      <Card ref={reportRef}>
        <KpiRow>
          <KpiCard>
            <KpiIcon>
              <FaClipboardList />
            </KpiIcon>
            <div>
              <KpiLabel>Credit Orders</KpiLabel>
              <KpiValue>{report.total_credit_orders || 0}</KpiValue>
            </div>
          </KpiCard>
          <KpiCard>
            <KpiIcon>
              <FaUserFriends />
            </KpiIcon>
            <div>
              <KpiLabel>Customers</KpiLabel>
              <KpiValue>{report.customer_count || 0}</KpiValue>
            </div>
          </KpiCard>
          <KpiCard>
            <KpiIcon>
              <FaMoneyBillWave />
            </KpiIcon>
            <div>
              <KpiLabel>Credit Amount</KpiLabel>
              <KpiValue>{fmtCurrency(report.total_credit_amount)}</KpiValue>
            </div>
          </KpiCard>
        </KpiRow>

        {loading ? (
          <Center>Loading…</Center>
        ) : !hasData ? (
          <Center>No credit payments for this date.</Center>
        ) : (
          <>
            {summaryRows.length > 0 && (
              <Section>
                <SectionTitle>Credit by Customer</SectionTitle>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Customer</th>
                        <th>Phone</th>
                        <th>Orders</th>
                        <th>Credit Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryRows.map((row, idx) => (
                        <tr key={`${row?.partyid ?? "guest"}-${idx}`}>
                          <td>{idx + 1}</td>
                          <td>{getCustomerName(row)}</td>
                          <td>{getCustomerPhone(row) || "-"}</td>
                          <td>{row?.total_orders || 0}</td>
                          <td>{fmtCurrency(row?.total_credit_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              </Section>
            )}

            <Section>
              <SectionTitle>Credit Payment Details</SectionTitle>
              <OrderGrid>
                {report.orders.map((order, idx) => {
                  const splitLines = formatSplitLines(order?.split);
                  const isSplit = String(order?.credit_type || "")
                    .trim()
                    .toLowerCase() === "split";
                  return (
                    <OrderCard key={`${order?.id}-${idx}`} onClick={() => setDetailOrder(order)}>
                      <OrderTop>
                        <div>
                          <OrderId>#{order?.ordercode}</OrderId>
                          <OrderCustomer>{getCustomerName(order)}</OrderCustomer>
                        </div>
                        <OrderAmount>{fmtCurrency(order?.credit_amount)}</OrderAmount>
                      </OrderTop>
                      <OrderMeta>
                        <span>Bill: {fmtCurrency(order?.bill_amount)}</span>
                        <span>Mode: {getPaymentModeLabel(order)}</span>
                      </OrderMeta>
                      <OrderMeta>
                        <span>{order?.table_name ? `Table: ${order.table_name}` : ""}</span>
                        <span>{isSplit ? "Split credit" : "Full credit"}</span>
                      </OrderMeta>
                      {splitLines.map((line) => (
                        <OrderLine key={`${order?.id}-${line}`}>{line}</OrderLine>
                      ))}
                    </OrderCard>
                  );
                })}
              </OrderGrid>
            </Section>
          </>
        )}

        <Actions style={{ marginTop: 12 }}>
          <PrimaryBtn onClick={exportExcel}>
            <FaFileExcel /> Excel
          </PrimaryBtn>
          <PrimaryBtn onClick={exportPdf}>
            <FaDownload /> PDF
          </PrimaryBtn>
          <PrimaryBtn onClick={handlePrint}>
            <FaPrint /> Thermal Print
          </PrimaryBtn>
          <PrimaryBtn onClick={handleUsbPrint}>
            <FaPrint /> Thermal Print via USB
          </PrimaryBtn>
        </Actions>
      </Card>

      {detailOrder && (
        <ModalOverlay onClick={() => setDetailOrder(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Credit Payment Details</ModalTitle>
              <IconBtn onClick={() => setDetailOrder(null)}>
                <FaTimes />
              </IconBtn>
            </ModalHeader>
            <ModalBody>
              <DetailLine>Order: #{detailOrder.ordercode}</DetailLine>
              <DetailLine>Customer: {getCustomerName(detailOrder)}</DetailLine>
              <DetailLine>Credit Amount: {fmtCurrency(detailOrder.credit_amount)}</DetailLine>
              <DetailLine>Bill Total: {fmtCurrency(detailOrder.bill_amount)}</DetailLine>
              <DetailLine>Payment Mode: {getPaymentModeLabel(detailOrder)}</DetailLine>
              {detailOrder?.table_name && <DetailLine>Table: {detailOrder.table_name}</DetailLine>}
              {detailOrder?.created_at && (
                <DetailLine>Date: {new Date(detailOrder.created_at).toLocaleString("en-IN")}</DetailLine>
              )}
              {getCustomerPhone(detailOrder) && <DetailLine>Phone: {getCustomerPhone(detailOrder)}</DetailLine>}
              {getCustomerCompany(detailOrder) && (
                <DetailLine>Company: {getCustomerCompany(detailOrder)}</DetailLine>
              )}
              {formatSplitLines(detailOrder?.split).length > 0 && (
                <DetailBlock>
                  {formatSplitLines(detailOrder?.split).map((line) => (
                    <DetailSplit key={line}>{line}</DetailSplit>
                  ))}
                </DetailBlock>
              )}
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default CreditPaymentReportPage;

const today = () => toISO(new Date());
const toISO = (d) => toLocalYmd(d);

const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;
const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: ${COLOR.text};
`;
const Sub = styled.p`
  margin: 6px 0 0;
  color: ${COLOR.muted};
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const NavBtn = styled.button`
  border: 1px solid ${COLOR.line};
  background: ${COLOR.card};
  color: ${COLOR.text};
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: ${COLOR.card};
  font-size: 13px;
  color: ${COLOR.muted};
  input {
    border: none;
    outline: none;
    font-size: 13px;
    color: ${COLOR.text};
    background: transparent;
  }
`;
const PrimaryBtn = styled.button`
  background: ${COLOR.primary};
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 16px;
`;
const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;
const KpiCard = styled.div`
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
  display: flex;
  gap: 10px;
  align-items: center;
`;
const KpiIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: #eef2ff;
  color: ${COLOR.primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const KpiLabel = styled.div`
  color: ${COLOR.muted};
  font-size: 12px;
  font-weight: 700;
`;
const KpiValue = styled.div`
  color: ${COLOR.text};
  font-size: 16px;
  font-weight: 800;
  margin-top: 2px;
`;
const Center = styled.div`
  padding: 24px 0;
  text-align: center;
  color: ${COLOR.muted};
  font-weight: 600;
`;
const Section = styled.div`
  margin-top: 16px;
`;
const SectionTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 800;
  color: ${COLOR.text};
`;
const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${COLOR.line};
    font-size: 13px;
    text-align: left;
  }
  th {
    background: #f8fafc;
    font-weight: 700;
  }
`;
const OrderGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;
const OrderCard = styled.button`
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
  background: #fff;
  text-align: left;
  cursor: pointer;
`;
const OrderTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
`;
const OrderId = styled.div`
  font-weight: 800;
  color: ${COLOR.text};
`;
const OrderCustomer = styled.div`
  font-size: 12px;
  color: ${COLOR.muted};
  margin-top: 4px;
`;
const OrderAmount = styled.div`
  font-weight: 800;
  color: ${COLOR.primary};
`;
const OrderMeta = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  color: ${COLOR.text};
  font-size: 12px;
  margin-top: 6px;
`;
const OrderLine = styled.div`
  font-size: 12px;
  margin-top: 4px;
  color: ${COLOR.text};
`;
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 999;
`;
const ModalCard = styled.div`
  background: #fff;
  border-radius: 16px;
  max-width: 480px;
  width: 100%;
  padding: 16px;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: ${COLOR.text};
`;
const IconBtn = styled.button`
  border: none;
  background: transparent;
  color: ${COLOR.muted};
  cursor: pointer;
`;
const ModalBody = styled.div`
  margin-top: 10px;
  display: grid;
  gap: 6px;
  font-size: 13px;
  color: ${COLOR.text};
`;
const DetailLine = styled.div``;
const DetailBlock = styled.div`
  border-top: 1px solid ${COLOR.line};
  margin-top: 6px;
  padding-top: 6px;
  display: grid;
  gap: 4px;
`;
const DetailSplit = styled.div`
  font-weight: 600;
`;
